// src/widgets/widgets/PaymentStreak.tsx
import { useMemo } from 'react';
import { Paper, Text, Group, RingProgress, Stack, Center } from '@mantine/core';
import { IconFlame, IconTrophy } from '@tabler/icons-react';
import type { WidgetProps } from '../types';

function PaymentStreak({ bills }: WidgetProps) {
	const streakData = useMemo(() => {
		const totalBills = bills.length;
		const paidBills = bills.filter((b) => b.paid).length;
		const percentage = totalBills > 0 ? (paidBills / totalBills) * 100 : 0;

		// Calculate "streak" - consecutive paid bills from the top of the list
		let streak = 0;
		for (const bill of bills) {
			if (bill.paid) {
				streak++;
			} else {
				break;
			}
		}

		return { paidBills, totalBills, percentage, streak };
	}, [bills]);

	const getStreakColor = () => {
		if (streakData.percentage >= 80) return 'green';
		if (streakData.percentage >= 50) return 'yellow';
		return 'orange';
	};

	const getStreakMessage = () => {
		if (streakData.totalBills === 0) return 'Add some bills to track!';
		if (streakData.percentage === 100) return 'Perfect! All bills paid!';
		if (streakData.percentage >= 80) return 'Almost there! Keep going!';
		if (streakData.percentage >= 50) return 'Good progress!';
		return 'Keep paying those bills!';
	};

	return (
		<Paper withBorder p='md' radius='md'>
			<Group gap='xs' mb='md'>
				<IconFlame size={18} color='var(--mantine-color-orange-6)' />
				<Text fw={600}>Payment Progress</Text>
			</Group>

			<Group justify='center' gap='xl'>
				<RingProgress
					size={100}
					thickness={10}
					roundCaps
					sections={[
						{
							value: streakData.percentage,
							color: getStreakColor(),
						},
					]}
					label={
						<Center>
							<Stack gap={0} align='center'>
								<Text fw={700} size='lg'>
									{Math.round(streakData.percentage)}%
								</Text>
							</Stack>
						</Center>
					}
				/>

				<Stack gap='xs'>
					<Group gap='xs'>
						<IconTrophy
							size={16}
							color='var(--mantine-color-yellow-6)'
						/>
						<Text size='sm'>
							<Text span fw={600}>
								{streakData.paidBills}
							</Text>{' '}
							of{' '}
							<Text span fw={600}>
								{streakData.totalBills}
							</Text>{' '}
							paid
						</Text>
					</Group>
					<Text size='xs' c='dimmed'>
						{getStreakMessage()}
					</Text>
				</Stack>
			</Group>
		</Paper>
	);
}

export default PaymentStreak;
